"use client";

import { memo, useCallback, useState } from "react";
import { Handle, NodeResizer, Position, useReactFlow } from "@xyflow/react";
import type { NodeProps } from "@xyflow/react";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale } from "@/lib/funnel-planner/schema";
import {
  directionForFunnelLocale,
  formatFunnelNumber,
  funnelText,
  stageTypeLabel,
  textAlignForFunnelLocale,
} from "@/lib/funnel-planner/i18n";
import {
  ALL_STAGE_TYPE_IDS,
  getStageTypeMeta as getCatalogStageTypeMeta,
  type StageTypeMeta,
} from "@/lib/funnel-planner/stageTypes";
import { StageTypePicker } from "@/components/funnel-planner/ui/StageTypePicker";
import {
  STAGE_NODE_DEFAULT_HEIGHT,
  STAGE_NODE_DEFAULT_WIDTH,
  STAGE_NODE_MAX_HEIGHT,
  STAGE_NODE_MAX_WIDTH,
  STAGE_NODE_MIN_HEIGHT,
  STAGE_NODE_MIN_WIDTH,
  clampNodeDimension,
  normalizeNodeColor,
} from "./node-visuals";

export type StageNodeData = {
  label: string;
  stageType: string;
  conversionRate: number;
  volume?: number;
  isSource?: boolean;
  url?: string;
  publishedStatus?: "draft" | "live" | "paused" | string;
  trackingStatus?: "none" | "planned" | "installed" | "verified" | "broken" | string;
  waitDays?: number;
  dueInDays?: number;
  actualVolume?: number;
  actualCvr?: number;
  owner?: string;
  color?: string;
  boxWidth?: number;
  boxHeight?: number;
  optionalStage?: boolean;
  nodeKind?: "stage" | "branch" | "retargeting" | "wait" | "survey" | string;
  onDelete?: (id: string) => void;
  onOpenMenu?: (id: string) => void;
  content?: FunnelPlannerContent;
  locale?: AppLocale;
};

const GROUP_COLORS: Record<StageTypeMeta["group"], string> = {
  acquisition: "#38bdf8",
  nurture: "#a78bfa",
  conversion: "#34d399",
  retention: "#f59e0b",
  plg: "#22d3ee",
  utility: "#94a3b8",
};

function stageMeta(type?: string) {
  return getCatalogStageTypeMeta(type ?? "custom");
}

function nodeColor(type?: string) {
  const meta = stageMeta(type);
  return GROUP_COLORS[meta.group] ?? "#94a3b8";
}

function nodeIcon(type?: string) {
  return stageMeta(type).icon;
}

function badgeLabel(value: unknown): string | null {
  if (value === undefined || value === null || value === "") return null;
  return String(value);
}

function Chip({
  children,
  color,
  background,
  maxWidth,
}: {
  children: React.ReactNode;
  color: string;
  background: string;
  maxWidth?: number;
}) {
  return (
    <span
      style={{
        borderRadius: 999,
        background,
        color,
        padding: "2px 6px",
        fontSize: 9,
        fontWeight: 700,
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

export const StageNode = memo(function StageNode({ id, data, selected }: NodeProps) {
  const d = data as StageNodeData;
  const { updateNodeData } = useReactFlow();
  const color = normalizeNodeColor(d.color) ?? nodeColor(d.stageType);
  const nodeWidth = clampNodeDimension(d.boxWidth, STAGE_NODE_MIN_WIDTH, STAGE_NODE_MAX_WIDTH, STAGE_NODE_DEFAULT_WIDTH);
  const nodeHeight = clampNodeDimension(d.boxHeight, STAGE_NODE_MIN_HEIGHT, STAGE_NODE_MAX_HEIGHT, STAGE_NODE_DEFAULT_HEIGHT);
  const [editingName, setEditingName] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const content = d.content;
  const locale = d.locale ?? "en";
  const nodeDirection = directionForFunnelLocale(locale);
  const nodeTextAlign = textAlignForFunnelLocale(locale);
  const inlineEnd = nodeDirection === "rtl" ? "left" : "right";
  const inlineStart = nodeDirection === "rtl" ? "right" : "left";
  const t = (key: string, fallback: string, values?: Record<string, string | number>) =>
    content ? funnelText(content, key, fallback, values) : fallback;
  const localStageLabel = (type: string | undefined) =>
    content ? stageTypeLabel(content, type, stageMeta(type).label) : stageMeta(type).label;

  const handleNameBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value.trim();
    updateNodeData(id, { label: val || d.label });
    setEditingName(false);
  }, [id, d.label, updateNodeData]);

  const handleNameKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.currentTarget.blur();
    }
  }, []);

  const handleCvrChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (!isNaN(val) && val >= 0.1 && val <= 100) {
      updateNodeData(id, { conversionRate: val });
    }
  }, [id, updateNodeData]);

  const handleResize = useCallback((_event: unknown, params: { width: number; height: number; direction?: number[] }) => {
    const shouldUpdateWidth = params.direction ? params.direction[0] !== 0 : true;
    const shouldUpdateHeight = params.direction ? params.direction[1] !== 0 : true;
    updateNodeData(id, {
      boxWidth: shouldUpdateWidth
        ? clampNodeDimension(params.width, STAGE_NODE_MIN_WIDTH, STAGE_NODE_MAX_WIDTH, STAGE_NODE_DEFAULT_WIDTH)
        : nodeWidth,
      boxHeight: shouldUpdateHeight
        ? clampNodeDimension(params.height, STAGE_NODE_MIN_HEIGHT, STAGE_NODE_MAX_HEIGHT, STAGE_NODE_DEFAULT_HEIGHT)
        : nodeHeight,
    });
  }, [id, nodeHeight, nodeWidth, updateNodeData]);

  const handleTypeSelect = useCallback((type: string) => {
    updateNodeData(id, {
      stageType: type,
      conversionRate: d.conversionRate || stageMeta(type).defaultCvr,
    });
    setShowTypeMenu(false);
  }, [id, d.conversionRate, updateNodeData]);

  const handleDelete = useCallback(() => {
    if (d.onDelete) d.onDelete(id);
  }, [id, d]);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    d.onOpenMenu?.(id);
  }, [d, id]);

  const volume = d.volume ?? 0;
  const nodeTextColor = "var(--color-text, #e2e8f0)";
  const nodeMutedColor = "var(--color-muted, #cbd5e1)";
  const nodeInputBg = "rgba(15, 23, 42, 0.86)";
  const publishedStatus = badgeLabel(d.publishedStatus);
  const trackingStatus = badgeLabel(d.trackingStatus);
  const owner = badgeLabel(d.owner);
  const waitDays = typeof d.waitDays === "number" && d.waitDays > 0 ? d.waitDays : null;
  const dueInDays = typeof d.dueInDays === "number" && d.dueInDays >= 0 ? d.dueInDays : null;

  return (
    <div
      dir={nodeDirection}
      data-funnel-planner-node-direction={nodeDirection}
      style={{
        width: nodeWidth,
        minHeight: nodeHeight,
        borderRadius: 10,
        border: selected ? `2px solid ${color}` : `1.5px solid ${color}33`,
        background: "var(--fp-card-surface-strong, #111827)",
        boxShadow: selected
          ? `0 0 0 3px ${color}30, 0 4px 16px rgba(0,0,0,0.14)`
          : "0 2px 8px rgba(0,0,0,0.10)",
        overflow: "visible",
        cursor: "default",
        position: "relative",
        direction: nodeDirection,
        textAlign: nodeTextAlign,
        "--funnel-planner-text-align": nodeTextAlign,
        "--funnel-planner-direction": nodeDirection,
      } as React.CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
    >
      <NodeResizer
        isVisible={Boolean(selected)}
        minWidth={STAGE_NODE_MIN_WIDTH}
        minHeight={STAGE_NODE_MIN_HEIGHT}
        maxWidth={STAGE_NODE_MAX_WIDTH}
        maxHeight={STAGE_NODE_MAX_HEIGHT}
        color={color}
        onResize={handleResize}
        lineClassName="funnel-node-resize-line"
        handleClassName="funnel-node-resize-handle"
        handleStyle={{
          width: 9,
          height: 9,
          border: `1px solid ${color}`,
          background: "var(--color-canvas-bg, #0f172a)",
        }}
        lineStyle={{
          borderColor: color,
        }}
      />
      <div style={{ height: 4, background: color, borderRadius: "8px 8px 0 0" }} />

      {d.onOpenMenu && (
        <button
          type="button"
          onClick={handleOpenMenu}
          title={t("canvasFunnelStageTitle", "Funnel Stage")}
          aria-label={t("canvasFunnelStageTitle", "Funnel Stage")}
          className="nodrag nopan"
          style={{
            position: "absolute",
            top: 6,
            [inlineStart]: 6,
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "rgba(15,23,42,0.78)",
            border: `1px solid ${color}66`,
            color: nodeTextColor,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            zIndex: 11,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-.793.793-2.828-2.828.793-.793Z" />
            <path d="m11.379 5.793 2.828 2.828-7.5 7.5H3.879v-2.828l7.5-7.5Z" />
          </svg>
        </button>
      )}

      <button
        type="button"
        onClick={handleDelete}
        title={t("canvasStageRemoveTitle", "Remove stage")}
        aria-label={t("canvasStageRemoveAria", "Remove this stage")}
        className="nodrag nopan"
        style={{
          position: "absolute",
          top: 6,
          [inlineEnd]: 6,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "transparent",
          border: "none",
          color: "#94a3b8",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          lineHeight: 1,
          padding: 0,
          zIndex: 10,
        }}
        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; }}
        onMouseLeave={e => { e.currentTarget.style.color = "#94a3b8"; }}
      >
        x
      </button>

      <div style={{ padding: "8px 10px 10px" }}>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setShowTypeMenu(v => !v)}
            title={t("canvasStageTypeChangeTitle", "Stage type - click to change")}
            className="nodrag nopan"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              maxWidth: Math.max(120, nodeWidth - 40),
              background: color + "18",
              border: "none",
              borderRadius: 4,
              padding: "2px 6px",
              cursor: "pointer",
              marginBottom: 6,
              textAlign: nodeTextAlign,
            }}
          >
            <span style={{ fontSize: 10, fontWeight: 800, color }}>{nodeIcon(d.stageType)}</span>
            <span style={{ fontSize: 9, fontWeight: 700, color, lineHeight: 1.4, textTransform: "uppercase", letterSpacing: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {localStageLabel(d.stageType)}
            </span>
            <span style={{ fontSize: 8, color, opacity: 0.7, marginInlineStart: 1 }}>v</span>
          </button>

          {showTypeMenu && content && (
            <StageTypePicker
              value={d.stageType ?? "custom"}
              onChange={handleTypeSelect}
              onClose={() => setShowTypeMenu(false)}
              content={content}
            />
          )}
          {showTypeMenu && !content && (
            <div
              className="nodrag nopan"
              dir={nodeDirection}
              style={{
                position: "absolute",
                top: "100%",
                [inlineStart]: 0,
                zIndex: 999,
                background: "var(--color-panel, #1e293b)",
                border: "1px solid var(--color-border-mid, #475569)",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                padding: "4px 0",
                minWidth: 220,
                maxHeight: 320,
                overflowY: "auto",
                direction: nodeDirection,
                textAlign: nodeTextAlign,
              }}
            >
              {ALL_STAGE_TYPE_IDS.map(type => {
                const meta = stageMeta(type);
                const optionColor = GROUP_COLORS[meta.group] ?? "#6b7280";
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleTypeSelect(type)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      padding: "6px 12px",
                      background: d.stageType === type ? optionColor + "18" : "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 11,
                      color: nodeTextColor,
                      textAlign: nodeTextAlign,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(59,130,246,0.18)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = d.stageType === type ? optionColor + "18" : "transparent"; }}
                  >
                    <span style={{ width: 22, textAlign: "center", color: optionColor, fontWeight: 800 }}>{meta.icon}</span>
                    {meta.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {editingName ? (
          <input
            autoFocus
            dir={nodeDirection}
            defaultValue={d.label}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKey}
            className="nodrag nopan"
            style={{
              width: "100%",
              fontSize: 13,
              fontWeight: 600,
              border: `1.5px solid ${color}`,
              borderRadius: 4,
              padding: "2px 6px",
              background: nodeInputBg,
              color: nodeTextColor,
              caretColor: nodeTextColor,
              colorScheme: "dark",
              outline: "none",
              marginBottom: 8,
              boxSizing: "border-box",
              direction: nodeDirection,
              textAlign: nodeTextAlign,
            }}
          />
        ) : (
          <div
            onClick={() => setEditingName(true)}
            title={t("canvasClickToEditName", "Click to edit name")}
            className="nodrag nopan"
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: d.label ? nodeTextColor : nodeMutedColor,
              lineHeight: 1.3,
              marginBottom: 8,
              cursor: "text",
              overflow: "visible",
              textOverflow: "clip",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              fontStyle: d.label ? "normal" : "italic",
              padding: "3px 2px",
              borderRadius: 4,
              border: "1.5px solid transparent",
              minHeight: 24,
              direction: nodeDirection,
              textAlign: nodeTextAlign,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = color + "66"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "transparent"; }}
          >
            {d.label || t("canvasClickToName", "Click to name...")}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span
            title={t("canvasCvrTitle", "Conversion Rate - percent of visitors who proceed to the next stage")}
            style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0, cursor: "help" }}
          >
            CVR
          </span>
          <input
            type="number"
            min={0.1}
            max={100}
            step={0.5}
            value={d.conversionRate}
            onChange={handleCvrChange}
            className="nodrag nopan"
            style={{
              width: 54,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border, #e2e8f0)",
              borderRadius: 4,
              padding: "2px 5px",
              background: nodeInputBg,
              color: nodeTextColor,
              caretColor: nodeTextColor,
              colorScheme: "dark",
              outline: "none",
            }}
          />
          <span style={{ fontSize: 10, color: "#94a3b8" }}>%</span>
        </div>

        <div
          title={t("canvasVolumeTitle", "{count} visitors reach this stage per month", {
            count: formatFunnelNumber(volume, locale),
          })}
          style={{
            display: "inline-flex",
            alignItems: "center",
            background: color + "18",
            color,
            borderRadius: 4,
            padding: "2px 7px",
            fontSize: 10,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.4,
            cursor: "default",
          }}
        >
          {formatFunnelNumber(volume, locale)}
          <span title={t("canvasPerMonthTitle", "Per month")} style={{ fontWeight: 400, opacity: 0.7, marginInlineStart: 2, cursor: "help" }}>
            {t("perMonthLabel", "/mo")}
          </span>
        </div>

        {(d.optionalStage || d.nodeKind || publishedStatus || trackingStatus || owner || waitDays || dueInDays !== null) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
            {d.nodeKind && d.nodeKind !== "stage" && (
              <Chip color="#93c5fd" background="rgba(96,165,250,0.14)">{d.nodeKind}</Chip>
            )}
            {d.optionalStage && (
              <Chip color="#cbd5e1" background="rgba(148,163,184,0.14)">optional</Chip>
            )}
            {publishedStatus && (
              <Chip color="#86efac" background="rgba(34,197,94,0.13)">{publishedStatus}</Chip>
            )}
            {trackingStatus && (
              <Chip color="#d8b4fe" background="rgba(168,85,247,0.13)">{trackingStatus}</Chip>
            )}
            {waitDays !== null && (
              <Chip color="#fcd34d" background="rgba(245,158,11,0.13)">{waitDays}d wait</Chip>
            )}
            {dueInDays !== null && (
              <Chip color="#fde68a" background="rgba(245,158,11,0.13)">due {dueInDays}d</Chip>
            )}
            {owner && (
              <Chip color="#7dd3fc" background="rgba(14,165,233,0.13)" maxWidth={88}>{owner}</Chip>
            )}
          </div>
        )}
      </div>

      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 10,
          height: 10,
          background: "#e2e8f0",
          border: `2px solid ${color}`,
          borderRadius: "50%",
          left: -6,
        }}
      />

      <Handle
        type="source"
        position={Position.Right}
        style={{
          width: 10,
          height: 10,
          background: color,
          border: "2px solid white",
          borderRadius: "50%",
          right: -6,
          boxShadow: "0 0 0 1px " + color + "55",
        }}
      />
    </div>
  );
});
